/**
 * React Hook for WebSocket Real-Time Collaboration
 * 
 * Provides a convenient way to use WebSocket service in React components
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import WebSocketService, { 
  Layer, 
  User, 
  CursorPosition, 
  ConnectionStatus,
  WebSocketServiceCallbacks 
} from '../services/websocketService';

export interface UseWebSocketReturn {
  // Connection
  isConnected: boolean;
  connectionStatus: ConnectionStatus;
  connect: () => void;
  disconnect: () => void;
  
  // Layer operations
  sendLayerCreate: (layerData: Omit<Layer, 'id' | 'created_at' | 'updated_at' | 'created_by'>) => void;
  sendLayerUpdate: (layerId: string, updates: Partial<Layer>) => void;
  sendLayerDelete: (layerId: string) => void;
  
  // Collaboration features
  sendCursorMove: (position: CursorPosition, pageNumber?: number) => void;
  sendLayerSelect: (layerId?: string) => void;
  
  // State
  activeUsers: User[];
  remoteCursors: Map<number, { user: User; position: CursorPosition; pageNumber?: number }>;
  remoteSelections: Map<number, { user: User; layerId?: string }>;
}

export function useWebSocket(
  layerDocId: string | undefined,
  callbacks: WebSocketServiceCallbacks = {}
): UseWebSocketReturn {
  const wsRef = useRef<WebSocketService | null>(null);
  const callbacksRef = useRef<WebSocketServiceCallbacks>(callbacks);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [activeUsers, setActiveUsers] = useState<User[]>([]);
  const [remoteCursors, setRemoteCursors] = useState(
    new Map<number, { user: User; position: CursorPosition; pageNumber?: number }>()
  );
  const [remoteSelections, setRemoteSelections] = useState(
    new Map<number, { user: User; layerId?: string }>()
  );
  
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  // Handle user joined
  const handleUserJoined = useCallback((user: User) => {
    setActiveUsers((prev) => {
      if (prev.some((u) => u.id === user.id)) {
        return prev;
      }
      return [...prev, user];
    });
    callbacksRef.current.onUserJoined?.(user);
  }, []);
  
  // Handle user left
  const handleUserLeft = useCallback((user: User) => {
    setActiveUsers((prev) => prev.filter((u) => u.id !== user.id));
    setRemoteCursors((prev) => {
      const newMap = new Map(prev);
      newMap.delete(user.id);
      return newMap;
    });
    setRemoteSelections((prev) => {
      const newMap = new Map(prev);
      newMap.delete(user.id);
      return newMap;
    });
    callbacksRef.current.onUserLeft?.(user);
  }, []);
  
  // Handle cursor moved
  const handleCursorMoved = useCallback((user: User, position: CursorPosition, pageNumber?: number) => {
    setRemoteCursors((prev) => {
      const newMap = new Map(prev);
      newMap.set(user.id, { user, position, pageNumber });
      return newMap;
    });
    callbacksRef.current.onCursorMoved?.(user, position, pageNumber);
  }, []);
  
  // Handle layer selected
  const handleLayerSelected = useCallback((user: User, layerId?: string) => {
    setRemoteSelections((prev) => {
      const newMap = new Map(prev);
      newMap.set(user.id, { user, layerId });
      return newMap;
    });
    callbacksRef.current.onLayerSelected?.(user, layerId);
  }, []);
  
  // Handle connection status change
  const handleConnectionStatusChange = useCallback((status: ConnectionStatus) => {
    setConnectionStatus(status);
    setIsConnected(status === 'connected');
    callbacksRef.current.onConnectionStatusChange?.(status);
  }, []);

  const handlePresenceSync = useCallback((users: User[]) => {
    setActiveUsers(users);
    callbacksRef.current.onPresenceSync?.(users);
  }, []);
  
  // Initialize WebSocket service
  useEffect(() => {
    if (!layerDocId) return;
    
    wsRef.current = new WebSocketService(layerDocId, {
      onUserJoined: handleUserJoined,
      onUserLeft: handleUserLeft,
      onCursorMoved: handleCursorMoved,
      onLayerSelected: handleLayerSelected,
      onConnectionStatusChange: handleConnectionStatusChange,
      onPresenceSync: handlePresenceSync,
      onLayerCreated: (...args) => callbacksRef.current.onLayerCreated?.(...args),
      onLayerUpdated: (...args) => callbacksRef.current.onLayerUpdated?.(...args),
      onLayerDeleted: (...args) => callbacksRef.current.onLayerDeleted?.(...args),
      onStateSync: (...args) => callbacksRef.current.onStateSync?.(...args),
      onError: (...args) => callbacksRef.current.onError?.(...args),
    });
    
    return () => {
      if (wsRef.current) {
        wsRef.current.disconnect();
        wsRef.current = null;
      }
    };
  }, [layerDocId, handleUserJoined, handleUserLeft, handleCursorMoved, handleLayerSelected, handleConnectionStatusChange, handlePresenceSync]);
  
  // Connect/disconnect functions
  const connect = useCallback(() => {
    wsRef.current?.connect();
  }, []);
  
  const disconnect = useCallback(() => {
    wsRef.current?.disconnect();
    setActiveUsers([]);
    setRemoteCursors(new Map());
    setRemoteSelections(new Map());
  }, []);
  
  // Layer operation functions
  const sendLayerCreate = useCallback((layerData: Omit<Layer, 'id' | 'created_at' | 'updated_at' | 'created_by'>) => {
    wsRef.current?.sendLayerCreate(layerData);
  }, []);
  
  const sendLayerUpdate = useCallback((layerId: string, updates: Partial<Layer>) => {
    wsRef.current?.sendLayerUpdate(layerId, updates);
  }, []);
  
  const sendLayerDelete = useCallback((layerId: string) => {
    wsRef.current?.sendLayerDelete(layerId);
  }, []);
  
  // Collaboration functions
  const sendCursorMove = useCallback((position: CursorPosition, pageNumber?: number) => {
    wsRef.current?.sendCursorMove(position, pageNumber);
  }, []);
  
  const sendLayerSelect = useCallback((layerId?: string) => {
    wsRef.current?.sendLayerSelect(layerId);
  }, []);
  
  return {
    // Connection
    isConnected,
    connectionStatus,
    connect,
    disconnect,
    
    // Layer operations
    sendLayerCreate,
    sendLayerUpdate,
    sendLayerDelete,
    
    // Collaboration features
    sendCursorMove,
    sendLayerSelect,
    
    // State
    activeUsers,
    remoteCursors,
    remoteSelections,
  };
}

export default useWebSocket;

